import { Component, OnDestroy, OnInit } from "@angular/core";
import { Router } from "@angular/router";
import { FormControl, FormGroup, FormBuilder } from '@angular/forms';
// services
import { MasterService } from "../../../services/master.service";
import { MessageService } from "../../../services/message.service";
import { StoreService } from "../../../services/store.service";
import { SecurityService } from "src/app/services/security.service";
import * as languageLibrary from '../../../services/language';
import { CashFlowSummary } from "src/app/models/cashflow-summary.model";
import { SalesSummary } from "src/app/models/sales-summary.model";
import { SalesSummaryTotal } from "src/app/models/sales-summary-total.model";
import { Lot } from "src/app/models/lot.model";
import moment from "moment";
import { Subscription } from "rxjs";
import { Receipt } from "src/app/models/receipt.model";
import { DATE_DEBOUNCE_MILLIS } from "src/app/constants/list";

@Component({
    selector: "cashflow-report",
    templateUrl: "./cashflow-report.component.html",
    styleUrls: ["./cashflow-report.component.scss"],
    standalone: false
})
export class CashFlowReportComponent implements OnInit, OnDestroy {

    /** define if is loading */
    protected loading: boolean = false

    // save the language
    protected language: string | null = localStorage.getItem('language') ? localStorage.getItem('language') : 'EN'

    // set all words
    protected words = languageLibrary.language

    /** */
    protected lots: Lot[] = []

    // The search form group
    protected form: FormGroup | null = null

    /** A list of summaries grouped by lot. **/
    protected summaries: CashFlowSummary[] = []

    /** The total cash flow summary for all lots. */
    protected summary: CashFlowSummary | null = null

    /** A list of sale summaries within the given time frame. */
    protected sales: SalesSummary[] | null = []

    /** The total sale summary for all lots. */
    protected saleSummary: SalesSummaryTotal | null = null

    /** Pre-computed receivables groups to quickly respond to interactivity changes.  */
    protected grouped: Object = {}

    // A debounce timer for search events. Used to wait for
    // the user to stop typing before we fetch.
    private watcher: ReturnType<typeof setTimeout> | null = null

    /** Listen for changes to the lot filter. */
    private subscriptions: Subscription[] = []

    /** If the report is filtered by lot this will have a positive value. */
    protected get lotId() {
        return parseInt(this.form.get('lotId').value)
    }

    /** A human readable date span based on the user selections. */
    protected get dateSpan() {
        const from = moment(this.form.get('from').value)
        const to = moment(this.form.get('to').value)

        const span = `${from.format('ddd MMM Do, YYYY')}`

        if (to.isSameOrBefore(from)) {
            return span
        }

        return `${span} - ${to.format('ddd MMM Do, YYYY')}`
    }

    constructor(private master: MasterService, private ms: MessageService, protected store: StoreService, private router: Router, private fb: FormBuilder, protected security: SecurityService) {
        this.form = this.fb.group({
            lotId: new FormControl<number>(0, []),
            from: new FormControl<string>(moment().format('YYYY-MM-DD'), []),
            to: new FormControl<string>(moment().format('YYYY-MM-DD'), [])
        })
    }

    ngOnInit() {
        this.subscriptions.push(
            this.form.get('lotId').valueChanges.subscribe((id) => {
                this.reset()
                this.fetch()
            })
        )

        this.subscriptions.push(
            this.form.get('from').valueChanges.subscribe((from) => {
                if (this.watcher) {
                    clearTimeout(this.watcher)
                    this.watcher = null
                }

                this.watcher = setTimeout(() => {
                    this.reset()
                    this.fetch()
                }, DATE_DEBOUNCE_MILLIS)
            })
        )

        this.subscriptions.push(
            this.form.get('to').valueChanges.subscribe((to) => {
                if (this.watcher) {
                    clearTimeout(this.watcher)
                    this.watcher = null
                }

                this.watcher = setTimeout(() => {
                    this.reset()
                    this.fetch()
                }, DATE_DEBOUNCE_MILLIS)
            })
        )

        this.reset()
        this.fetch()

        if (this.store && this.store.userAccount && this.store.userAccount['user'] &&
            this.store.userAccount['user'].lots) {
            this.lots = this.store.userAccount['user'].lots
        }
    }

    ngOnDestroy() {
        this.reset()

        if (this.watcher) {
            clearTimeout(this.watcher)
            this.watcher = null
        }

        for (const sub of this.subscriptions) {
            sub.unsubscribe()
        }
    }

    /**
     * Reset the pagination parameters and scroll to the top
    */
    private reset() {
        this.summaries = []
        this.summary = null
        this.sales = []
        this.saleSummary = null
        this.grouped = {}

        const list = document.querySelector('#listing')
        if (!list) {
            return
        }

        list.scrollTo({
            top: 0,
            left: 0,
            behavior: 'auto'
        })
    }

    /**
     * Get the summary and list of receivables needed for the report.
     */
    protected fetch = async () => {
        if (this.security.notPermitted('reports', 'cash flow', 'view')) {
            return
        }

        if (this.loading) { // already getting the data
            return
        }

        let lotParam = ''
        const id = parseInt(this.form.get('lotId').value)
        let prefix = '?'
        if (id && (!isNaN(id) && (id > 0))) {
            lotParam = `?lot=${id}`
            prefix = '&'
        }

        let fromParam = ''
        const from = this.form.get('from').value
        if (from) {
            fromParam = `${prefix}from=${moment(from).startOf('day').format()}`
            prefix = '&'
        }

        let toParam = ''
        const to = this.form.get('to').value
        if (to) {
            toParam = `${prefix}to=${moment(to).endOf('day').format()}`
        }

        this.loading = true
        await this.master.getAsync(`reports/cashflow${lotParam}${fromParam}${toParam}`).then(res => {
            this.loading = false

            if (!res || !res.data) {
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error ? res.data.error : this.words[this.language]['apiNoResponse'] });
                return
            }

            this.summaries = res.data.summary || []
            this.computeSummary()

            this.sales = res.data.sales || []
            this.saleSummary = this.computeSalesSummary(this.sales)

            this.constructGroups(res.data.receipts)
        })
    }

    /**
    * Sum all of the lot summary data to get the totals.
    */
    private computeSummary = () => {
        if (!this.summaries || (this.summaries.length <= 0)) {
            return
        }

        if (this.summaries.length === 1) {  // single lot?
            this.summary = null
            return
        }

        this.summary = new CashFlowSummary()
        this.summary.name = this.summaries[0].corporation

        for (const item of this.summaries) {
            this.summary.bhph += item.bhph
            this.summary.lates += item.lates
            this.summary.principal += item.principal
            this.summary.bhphCash += item.bhphCash
            this.summary.bhphCashCount += item.bhphCashCount
            this.summary.bhphCard += item.bhphCard
            this.summary.bhphCardCount += item.bhphCardCount
            this.summary.bhphAch += item.bhphAch
            this.summary.bhphAchCount += item.bhphAchCount
            this.summary.bhphOther += item.bhphOther
            this.summary.bhphOtherCount += item.bhphOtherCount
            this.summary.downs += item.downs
            this.summary.downsOutside += item.downsOutside
            this.summary.downCash += item.downCash
            this.summary.downCashCount += item.downCashCount
            this.summary.downCard += item.downCard
            this.summary.downCardCount += item.downCardCount
            this.summary.downAch += item.downAch
            this.summary.downAchCount += item.downAchCount
            this.summary.downOther += item.downOther
            this.summary.downOtherCount += item.downOtherCount
            this.summary.deposits += item.deposits
            this.summary.depositsOutside += item.depositsOutside
            this.summary.outside += item.outside
            this.summary.cash += item.cash
            this.summary.wholesale += item.wholesale
            this.summary.cpi += item.cpi
            this.summary.afterSale += item.afterSale
            this.summary.bounce += item.bounce
            this.summary.scheduled += item.scheduled
        }
    }

    /**
    * Sum all of the lot sale summary data to get the totals.
    */
    private computeSalesSummary = (sales: SalesSummary[]): SalesSummaryTotal => {
        if (!sales || (sales.length <= 0)) {
            return new SalesSummaryTotal()
        }

        const aSummary = new SalesSummaryTotal()
        for (const item of sales) {
            aSummary.total += item.price

            switch (item.typeKey) {
                case 'bhph':
                    aSummary.bhph += item.price
                    aSummary.payments += item.payments
                    break
                case 'outside':
                    aSummary.outside += item.price
                    break
                case 'wholesale':
                    aSummary.wholesale += item.price
                    break
                case 'cash':
                    aSummary.cash += item.price
                    break
            }
        }
        return aSummary
    }

    /**
     * Group the receipts for later display.
     * @param {Receipt[]} receipts A list of receipts within the given time frame.
     */
    private constructGroups = (receipts: Receipt[]) => {
        if (!receipts || (receipts.length <= 0)) {
            return
        }

        for (const receipt of receipts) {
            const key: string = `${receipt.corporateId}-${receipt.dealershipId}-${receipt.lotId}`
            if (!this.grouped.hasOwnProperty(key)) {
                this.grouped[key] = {
                    lot: receipt.payee,
                    total: 0,
                    count: 0,
                    saleCount: 0,
                    saleTotal: 0,
                    summary: this.summaries.find((item) => item.lotId == receipt.lotId),
                    cash: [],
                    cashTotal: 0,
                    wholesale: [],
                    wholesaleTotal: 0,
                    outside: [],
                    outsideTotal: 0,
                    bhphDeferred: [],
                    bhphDeferredTotal: 0,
                    bhphPaid: [],
                    bhphPaidTotal: 0,
                    saleSummary: this.computeSalesSummary(this.sales.filter((item) => item.lotId == receipt.lotId))
                }
            }

            this.grouped[key].total += receipt.amount - (receipt.refund || 0)
            this.grouped[key].count++

            if (!receipt.meta || !Array.isArray(receipt.meta) || (receipt.meta.length <= 0)) {
                continue
            }

            for (const line of receipt.meta) {
                const meta = {
                    buyer: receipt.payer,
                    sold: receipt.paid,
                    stock: line.stock,
                    vin: line.vin,
                    yearMakeModel: line.vehicle,
                    type: line.type,
                    category: line.category,
                    method: line.method,
                    source: line.source,
                    receiver: receipt.receiver,
                    number: receipt.number,
                    amount: line.amount
                }

                if ((line.category === 'mv loan') && (line.type !== 'payment')) {
                    meta.category = line.type
                }

                switch (receipt.sale_type.toLocaleLowerCase()) {
                    case 'cash':
                        this.grouped[key].cash.push(meta)
                        this.grouped[key].cashTotal += line.amount
                        break
                    case 'wholesale':
                        this.grouped[key].wholesale.push(meta)
                        this.grouped[key].wholesaleTotal += line.amount
                        break
                    case 'outside':
                        this.grouped[key].outside.push(meta)
                        this.grouped[key].outsideTotal += line.amount
                        break
                    case 'bhph':
                        switch (receipt.tax_method.toLocaleLowerCase()) {
                            case 'paid':
                                this.grouped[key].bhphPaid.push(meta)
                                this.grouped[key].bhphPaidTotal += line.amount
                                break
                            case 'deferred':
                                this.grouped[key].bhphDeferred.push(meta)
                                this.grouped[key].bhphDeferredTotal += line.amount
                                break
                        }
                        break
                }
            }

            this.grouped[key].saleCount = this.grouped[key].cash.length + this.grouped[key].outside.length +
                this.grouped[key].wholesale.length + this.grouped[key].bhphDeferred.length +
                this.grouped[key].bhphPaid.length
            this.grouped[key].saleTotal = this.grouped[key].cashTotal + this.grouped[key].outsideTotal +
                this.grouped[key].wholesaleTotal + this.grouped[key].bhphDeferredTotal +
                this.grouped[key].bhphPaidTotal

        }
    }

    /** Total cash flow for the given summary. */
    protected totalFlow(aSummary: CashFlowSummary) {
        if (!aSummary) {
            return 0
        }

        return (aSummary.bhph + aSummary.deposits + aSummary.downs +
            aSummary.depositsOutside + aSummary.downsOutside +
            aSummary.outside + aSummary.cash + aSummary.wholesale +
            aSummary.cpi + aSummary.afterSale + aSummary.bounce)
    }

    protected colorize = (aSummary: CashFlowSummary): string => {
        if (!aSummary || !aSummary.scheduled || (aSummary.scheduled <= 0)) {
            return 'secondary'
        }

        const total = this.totalFlow(aSummary)
        if (!total || (total <= 0)) {
            return 'secondary'
        }

        const ratio = (total / aSummary.scheduled) * 100
        if (ratio >= 105) {
            return 'success'
        }

        if (ratio >= 95) {
            return 'warning'
        }

        return 'danger'
    }

    /**
     * listen to output of navbar
    */
    protected listenerNav = (e): void => {
        switch (e['message']) {
            case 'changeLanguage':
                this.language = e['value']
                break
            case 'setPermissions':
                this.lots = this.store.userAccount['user'].lots
                this.reset()
                this.fetch()
                break
            case 'changeLot':
                break
        }
    }
}
